import type * as React from 'react'
import { NavLink } from 'react-router-dom'

import {
  Combobox,
  EmptyState,
  TagIcon,
  Tag,
  SearchIcon,
  Button,
  IconButton,
  SettingsIcon,
} from '@buffer-mono/popcorn'
import type { ButtonProps } from '@buffer-mono/popcorn'

import { useTagsQuery } from '~publish/hooks/useTags'
import { settingsTagsRoute } from '~publish/legacy/routes'

import { FilterButton } from '../FilterButton'

import styles from './FilterByTag.module.css'

import type { JSX } from 'react'

/**
 * Value used in URLs and internally to represent filtering for posts/ideas
 * without any tags. This human-readable value is converted to
 * `{ isEmpty: true }` in the TagComparator when sent to GraphQL.
 */
export const UNTAGGED_VALUE = 'untagged'

/**
 * Filters out the UNTAGGED_VALUE from an array of tag IDs.
 * Use this when passing selected tags to the composer to prevent
 * the special "untagged" filter from being applied as an actual tag.
 */
export function filterOutUntaggedId(tagIds: string[]): string[] {
  return tagIds.filter((id) => id !== UNTAGGED_VALUE)
}

/**
 * Must stay assignable to the schema's `TagComparator` input: callers pass
 * the built value straight into their query variables.
 */
type TagComparator = {
  isEmpty: boolean
  in: string[]
}

/**
 * Converts an array of selected tag IDs (including UNTAGGED_VALUE)
 * to a TagComparator object for use with the GraphQL TagComparator input.
 *
 * @param selectedTagIds - Array of tag IDs from the filter UI
 * @returns TagComparator object or undefined if no tags selected
 */
export function buildTagComparator(
  selectedTagIds: string[] | undefined,
): TagComparator | undefined {
  if (!selectedTagIds || selectedTagIds.length === 0) return undefined

  const hasUntagged = selectedTagIds.includes(UNTAGGED_VALUE)
  const tagIds = selectedTagIds.filter((id) => id !== UNTAGGED_VALUE)

  // Return undefined if no valid filter criteria
  if (!hasUntagged && tagIds.length === 0) return undefined

  return {
    isEmpty: hasUntagged,
    in: tagIds,
  }
}

type FilterByTagProps = Pick<ButtonProps, 'size' | 'variant'> & {
  /**
   * Selected tag ids
   */
  value: string[]
  onSelect: (tagIds: string[]) => void
  className?: string
  /**
   * Optional composable trigger. When provided, replaces the default
   * FilterButton trigger, similar to Radix's composable trigger pattern.
   */
  children?: React.ReactNode
}

type FilterByTagOption = {
  id: string
  name: string
  color: string
}

type FilterByTagViewProps = FilterByTagProps & {
  tags: readonly FilterByTagOption[]
  footerAction?: React.ReactNode
}

export const FilterByTagView = ({
  size = 'medium',
  variant = 'tertiary',
  value,
  onSelect,
  className,
  children,
  tags,
  footerAction,
  ...props
}: FilterByTagViewProps): JSX.Element => {
  return (
    <Combobox multiple value={value} onChange={onSelect}>
      <Combobox.Trigger className={className} {...props}>
        {children ?? (
          <FilterButton
            icon={<TagIcon />}
            count={value.length}
            variant={variant}
            size={size}
          >
            Tags
          </FilterButton>
        )}
      </Combobox.Trigger>
      <Combobox.Content>
        <Combobox.Input
          aria-label="Search tags"
          placeholder="Search tags"
          prefix={<SearchIcon />}
        />
        {/*
         * Guard the results list, not the whole content: cmdk unmounts
         * non-matching rows while filtering, so a browser-translated descendant
         * (a tag name, "Untagged", an empty state) would make React throw
         * NotFoundError on removeChild. Guarding the list protects every
         * churning row, while the search input and "Clear all" footer — which
         * don't unmount during filtering — stay translatable. Don't scope this
         * inward onto the tag names alone: that leaves "Untagged"/empty states
         * translatable and reintroduces the crash.
         */}
        <Combobox.List className={styles.list} translate="no">
          <Combobox.Empty>
            {tags.length === 0 ? (
              <EmptyState size="small" variant="primary">
                <EmptyState.Icon>
                  <TagIcon />
                </EmptyState.Icon>
                <EmptyState.Heading>No tags</EmptyState.Heading>
                <EmptyState.Description>
                  Use tags to organise your content
                </EmptyState.Description>
              </EmptyState>
            ) : (
              <EmptyState size="small">
                <EmptyState.Icon>
                  <SearchIcon />
                </EmptyState.Icon>
                <EmptyState.Description>No tags found</EmptyState.Description>
              </EmptyState>
            )}
          </Combobox.Empty>
          {tags.length > 0 && (
            <Combobox.Item
              key={UNTAGGED_VALUE}
              value={UNTAGGED_VALUE}
              keywords={['untagged', 'no tags', 'without tags']}
              className={styles.untaggedItem}
            >
              Untagged
            </Combobox.Item>
          )}
          {tags.map(({ id, name, color }) => (
            <Combobox.Item key={id} value={id} keywords={[name]}>
              <Tag color={color}>{name}</Tag>
            </Combobox.Item>
          ))}
        </Combobox.List>

        <Combobox.Footer className={styles.footer}>
          <Combobox.Clear>
            <Button variant="tertiary">Clear All</Button>
          </Combobox.Clear>
          {footerAction}
        </Combobox.Footer>
      </Combobox.Content>
    </Combobox>
  )
}

/**
 * Component to enable filtering by tags, using Combobox.
 * Supports a composable trigger via children — when provided, it replaces
 * the default FilterButton. Existing usages without children are unchanged.
 */
export const FilterByTag = (props: FilterByTagProps): JSX.Element => {
  const { tags } = useTagsQuery()
  return (
    <FilterByTagView
      {...props}
      tags={tags}
      footerAction={
        <IconButton
          variant="tertiary"
          label="Settings"
          tooltip="Settings"
          as={NavLink}
          to={settingsTagsRoute.route}
        >
          <SettingsIcon />
        </IconButton>
      }
    />
  )
}
